import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  User,
  Building2,
  Mail,
  Phone,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";

const FullContactForm = () => {
  const lang = useSelector((state) => state.language.lang);
  const location = useLocation();
  const data = location.state || {};

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  const text = {
    en: {
      title: "Send Us A Message",
      subtitle: "Tell us about your goals and we will contact you shortly.",
      name: "Full Name *",
      business: "Business Name",
      email: "Email *",
      phone: "Phone",
      service: "Interested In *",
      time: "Best Time To Contact",
      message: "Message *",
      submit: "Send Message",
      sending: "Sending...",
      success: "Thank you. Your enquiry has been sent successfully.",
      services: [
        "Business Coaching",
        "Business Consulting",
        "Investment Advisory",
        "Property Advisory",
        "General Enquiry",
      ],
      times: ["Morning", "Afternoon", "Evening"],
      errors: {
        fullName: "Please enter your full name",
        email: "Please enter your email",
        invalidEmail: "Please enter a valid email",
        service: "Please select a service",
        message: "Please enter your message",
      },
    },
    vi: {
      title: "Gửi Yêu Cầu Tư Vấn",
      subtitle: "Hãy chia sẻ nhu cầu của bạn và Maria sẽ liên hệ sớm nhất.",
      name: "Họ và Tên *",
      business: "Tên Doanh Nghiệp",
      email: "Email *",
      phone: "Điện Thoại",
      service: "Dịch Vụ Quan Tâm *",
      time: "Thời Gian Liên Hệ",
      message: "Nội Dung *",
      submit: "Gửi Yêu Cầu",
      sending: "Đang gửi...",
      success: "Yêu cầu của bạn đã được gửi thành công.",
      services: [
        "Tư Vấn Kinh Doanh",
        "Chiến Lược Doanh Nghiệp",
        "Tư Vấn Đầu Tư",
        "Tư Vấn Bất Động Sản",
        "Yêu Cầu Khác",
      ],
      times: ["Buổi sáng", "Buổi chiều", "Buổi tối"],
      errors: {
        fullName: "Vui lòng nhập họ tên",
        email: "Vui lòng nhập email",
        invalidEmail: "Email không hợp lệ",
        service: "Vui lòng chọn dịch vụ",
        message: "Vui lòng nhập nội dung",
      },
    },
  }[lang || "en"];

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    fullName: data.fullName || "",
    businessName: data.businessName || "",
    email: data.email || "",
    phone: data.phone || "",
    service: data.service || "",
    preferredTime: data.preferredTime || "",
    message: data.message || "",
  });

  const updateField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = text.errors.fullName;
    if (!form.email.trim()) e.email = text.errors.email;
    else if (!/\S+@\S+\.\S+/.test(form.email))
      e.email = text.errors.invalidEmail;
    if (!form.service) e.service = text.errors.service;
    if (!form.message.trim()) e.message = text.errors.message;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE,
        process.env.REACT_APP_EMAILJS_TEMPLATE,
        form,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );
      setSent(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f3ef]">
        <div className="bg-white p-10 rounded-3xl shadow-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold">{text.success}</h2>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-[#f7f3ef] min-h-screen py-20 px-6">
      <div className="max-w-6xl mx-auto rounded-xl my-8">
        {" "}
        <div className="mb-8">
          <button
            onClick={() => navigate("/contact")}
            className="
      inline-flex items-center gap-2
      px-4 py-2
      rounded-xl
      border border-[#e5d8cc]
      bg-white
      text-[#333]
      hover:bg-reddark
      hover:text-white
      hover:border-reddark
      transition-all duration-300
      shadow-sm
    "
          >
            <ArrowLeft size={18} />
            Back
          </button>
        </div>
        {/* ------------------ */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-reddark/5 rounded-full blur-3xl" />
        <div className="p-0 md:p-0">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[1px] bg-reddark" />

              <span className="text-xs uppercase tracking-[0.3em] text-reddark font-semibold">
                Maria Advisory
              </span>
            </div>

            <h1 className="font-[Crimson] text-3xl md:text-5xl text-[#111] leading-tight">
              {text.title}
            </h1>

            <p className="mt-4 text-[#666] leading-relaxed max-w-2xl">
              {text.subtitle}
            </p>
          </div>

          {/* ----------- */}
          <div className="h-2 bg-red-800" />
          <div className="py-4">
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label={text.name}
                value={form.fullName}
                error={errors.fullName}
                onChange={(v) => updateField("fullName", v)}
                icon={User}
              />
              <Field
                label={text.business}
                value={form.businessName}
                onChange={(v) => updateField("businessName", v)}
                icon={Building2}
              />
              <Field
                label={text.email}
                value={form.email}
                error={errors.email}
                onChange={(v) => updateField("email", v)}
                icon={Mail}
              />
              <Field
                label={text.phone}
                value={form.phone}
                onChange={(v) => updateField("phone", v)}
                icon={Phone}
              />
            </div>

            <div className="mt-8">
              <label className="font-semibold text-textGray">
                {text.service}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                {text.services.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateField("service", s)}
                    className={`
  group
  p-4
  rounded-2xl
  border
  text-sm
  font-medium
  transition-all
  duration-300
  text-left

  ${
    form.service === s
      ? `
        bg-reddark
        text-white
        border-reddark
        shadow-lg
      `
      : `
        bg-[#faf7f4]
        text-[#333]
        border-[#e5d8cc]
        hover:border-reddark
        hover:-translate-y-1
      `
  }
`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {errors.service && (
                <p className="text-red-600 text-sm mt-2">{errors.service}</p>
              )}
            </div>

            <div className="mt-8">
              <label className="font-semibold text-textGray">{text.time}</label>
              <div className="flex gap-3 mt-3 flex-wrap">
                {text.times.map((t) => (
                  <button
                    key={t}
                    onClick={() => updateField("preferredTime", t)}
                    className={`px-4 py-2 rounded-xl border ${form.preferredTime === t ? "bg-red-800 text-white" : "bg-[#f7f3ef] text-[#333] border-[#ddd]"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <label className="font-semibold text-textGray">
                {text.message}
              </label>
              <textarea
                rows={7}
                value={form.message}
                onChange={(e) => updateField("message", e.target.value)}
                className={`w-full mt-2 p-4 rounded-2xl border ${errors.message ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.message && (
                <p className="text-red-600 text-sm mt-2">{errors.message}</p>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              className="mt-8 bg-red-800 text-white px-8 py-4 rounded-2xl flex items-center gap-3"
            >
              <MessageSquare size={18} />
              {loading ? text.sending : text.submit}
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, error, icon: Icon }) => (
  <div>
    <label className="block mb-2 font-semibold text-textGray">{label}</label>
    <div className="relative">
      <Icon
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        size={18}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`
  w-full
  pl-12
  pr-4
  py-4
  rounded-2xl
  border
  bg-[#faf7f4]
  text-[#222]
  placeholder:text-[#999]
  transition-all
  focus:outline-none
  focus:border-reddark
  focus:bg-white

  ${error ? "border-red-500" : "border-[#e5d8cc]"}
`}
      />
    </div>
    {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
  </div>
);

export default FullContactForm;
